import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { SplashScreen } from "@capacitor/splash-screen";
import { env } from "./constants/env";
import { renderApp } from "./entry/render-app";
import { renderOfflineShell } from "./entry/render-offline-page";
import { renderSplash } from "./entry/render-splash";
import { retryConnection } from "./entry/retry-connection";
import { setupViewport } from "./entry/setup-viewport";
import { isLikelyChunkError } from "./utils/error/is-chunk-error";
import { initSentryLogging } from "./utils/sentry";
import { initServiceWorker } from "./utils/service-worker/workbox-service-worker";

// To enable some keyboard features on iOS, (previous/next)
if (Capacitor.getPlatform() === "ios") {
  Keyboard.setAccessoryBarVisible({ isVisible: true });
}

initSentryLogging();

let networkListenersAttached = false;

export const removeSplashScreen = () => {
  SplashScreen.hide().catch((error) => {
    console.error("Failed to hide splash screen", error);
  });
};

const bootstrap = async () => {
  const platform = Capacitor.getPlatform();
  const isNative = platform === "android" || platform === "ios";
  const remoteBaseUrl = env.VITE_REMOTE_APP_URL;

  // Neutralize Android edge-to-edge margins in the background. Every boot surface
  // (splash overlay, offline shell) is full-screen, so this must never block first paint.
  void setupViewport();

  // Commit the branded background before releasing the native splash so the WebView is
  // never empty during handoff. The animated logo is owned by <AppSplash> inside <App>,
  // which mounts once and plays through exactly once. We no longer block the app behind a
  // fixed timeout — the app loads in the background while <AppSplash> covers it, and the
  // splash lifts as soon as the app is ready (see components/splash/app-splash.tsx).
  renderSplash();

  // Old production shells disable automatic splash hiding. Release the native splash as
  // soon as the web background is committed so the app can finish loading underneath the
  // <AppSplash> overlay instead of behind the native logo.
  if (isNative) {
    removeSplashScreen();
  }

  const reloadOnChunkError = (errorMessage?: string) => {
    if (!errorMessage || !isLikelyChunkError(errorMessage)) {
      return;
    }
    if (sessionStorage.getItem("chunk-reload")) {
      return;
    }
    sessionStorage.setItem("chunk-reload", "true");
    window.location.reload();
  };

  window.addEventListener("error", (event) => {
    console.log("listening for error: ", event.message);
    reloadOnChunkError(event.message);
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    console.log("listening for unhandled rejection: ", reason);
    if (reason instanceof Error) {
      reloadOnChunkError(reason.message);
    } else if (typeof reason === "string") {
      reloadOnChunkError(reason);
    }
  });

  const attachNetworkListeners = () => {
    if (networkListenersAttached) {
      return;
    }
    networkListenersAttached = true;

    window.addEventListener("offline", () => {
      renderOfflineShell({
        isChecking: false,
        errorMessage: "Device appears to be offline.",
      }).catch((error) => {
        console.error("Failed to render offline shell", error);
      });
    });

    window.addEventListener("online", () => {
      retryConnection().catch((error) => {
        console.error("Failed to retry connection", error);
      });
    });
  };

  if (isNative && !navigator.onLine) {
    await renderOfflineShell({
      isChecking: false,
      errorMessage: "Device appears to be offline.",
    });
    attachNetworkListeners();
    initServiceWorker(remoteBaseUrl);
    return;
  }

  await renderApp();
  initServiceWorker(remoteBaseUrl);
  attachNetworkListeners();
};

bootstrap().catch((error) => {
  console.error("Failed to bootstrap NutriCoach", error);

  renderOfflineShell({
    isChecking: false,
    errorMessage: "NutriCoach could not start. Check your connection and try again.",
  })
    .catch((renderError) => {
      console.error("Failed to render the startup fallback", renderError);
    })
    .finally(removeSplashScreen);
});
